import { useTranslation } from 'react-i18next';
import React from 'react';

/**
 * Hook to generate multilingual SEO metadata
 * Returns properly translated and structured SEO tags
 */
export const useMultilingualSEO = (page, data = {}) => {
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;
  const SITE_URL = 'https://livinggeeta.com';

  const langConfig = {
    en: { name: 'English', locale: 'en_US', hreflang: 'en' },
    hi: { name: 'Hindi', locale: 'hi_IN', hreflang: 'hi' },
    ta: { name: 'Tamil', locale: 'ta_IN', hreflang: 'ta' },
    te: { name: 'Telugu', locale: 'te_IN', hreflang: 'te' },
    bn: { name: 'Bengali', locale: 'bn_IN', hreflang: 'bn' },
    kn: { name: 'Kannada', locale: 'kn_IN', hreflang: 'kn' },
    or: { name: 'Odia', locale: 'or_IN', hreflang: 'or' },
    as: { name: 'Assamese', locale: 'as_IN', hreflang: 'as' },
    gu: { name: 'Gujarati', locale: 'gu_IN', hreflang: 'gu' },
    ml: { name: 'Malayalam', locale: 'ml_IN', hreflang: 'ml' },
    es: { name: 'Spanish', locale: 'es_ES', hreflang: 'es' }
  };

  const currentLangConfig = langConfig[currentLang] || langConfig.en;

  // Since language is localStorage-based (not URL-based), all hreflang
  // tags point to the same canonical URL. This avoids pointing to 404s.
  const generateHreflangTags = (basePath) => {
    const canonicalHref = `${SITE_URL}${basePath}`;
    return [
      <link key="hreflang-x-default" rel="alternate" hrefLang="x-default" href={canonicalHref} />,
      ...Object.keys(langConfig).map(lang => (
        <link key={`hreflang-${lang}`} rel="alternate" hrefLang={langConfig[lang].hreflang} href={canonicalHref} />
      ))
    ];
  };

  // Canonical always points to the base URL (no language prefix)
  const getCanonicalUrl = (basePath) => `${SITE_URL}${basePath}`;

  // Generate BreadcrumbList structured data
  const generateBreadcrumbSD = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      ...(item.url ? { "item": item.url } : {})
    }))
  });

  return {
    currentLang,
    currentLangConfig,
    getCanonicalUrl,
    generateHreflangTags,
    generateBreadcrumbSD,
    SITE_URL
  };
};
